import { useCallback, useEffect, useRef } from "react"
import { meetings } from "services/api/meetingsClient"

export type MeetingEndReason = "ended" | "cancelled"

/** How often signed-in attendees re-read the meeting while in a custom call. */
export const MEETING_END_POLL_MS = 60000

type UseMeetingEndWatcherArgs = {
  meetingId: string | null | undefined
  /** Watch only while a call surface is live. */
  enabled: boolean
  /** Guests have no meeting endpoint to poll; they rely on realtime events only. */
  canPoll: boolean
  /** The end time the attendee loaded the call with; a later fetch that moves it earlier means the host ended early. */
  scheduledEndTime: string | null | undefined
  onEnded: (reason: MeetingEndReason) => void
}

/**
 * Ends the custom call when the host ends the meeting early or cancels it. Daily does not eject
 * attendees who are already connected when the room expires, so the shell has to act itself:
 * realtime `meeting.ended` / `meeting.cancelled` panel events arrive through the messages
 * notification socket, and signed-in attendees also poll the meeting so a missed event, or a
 * stack without realtime, still ends the call. A meeting simply running past its scheduled end
 * is left alone.
 */
export function useMeetingEndWatcher({ meetingId, enabled, canPoll, scheduledEndTime, onEnded }: UseMeetingEndWatcherArgs) {
  const onEndedRef = useRef(onEnded)
  onEndedRef.current = onEnded
  const firedRef = useRef(false)
  const scheduledEndRef = useRef<number | null>(null)

  useEffect(() => {
    firedRef.current = false
    const parsed = scheduledEndTime ? Date.parse(scheduledEndTime) : NaN
    scheduledEndRef.current = Number.isFinite(parsed) ? parsed : null
  }, [enabled, meetingId, scheduledEndTime])

  const fire = useCallback((reason: MeetingEndReason) => {
    if (firedRef.current) return
    firedRef.current = true
    onEndedRef.current(reason)
  }, [])

  const handleMeetingEvent = useCallback((event: { type?: string } | null | undefined) => {
    if (!enabled) return
    if (event?.type === "meeting.ended") fire("ended")
    else if (event?.type === "meeting.cancelled") fire("cancelled")
  }, [enabled, fire])

  useEffect(() => {
    if (!enabled || !canPoll || !meetingId) return
    let disposed = false
    const check = async () => {
      try {
        const meeting = await meetings.getMeeting(meetingId)
        if (disposed || !meeting) return
        if ((meeting as any).status === "cancelled") {
          fire("cancelled")
          return
        }
        const end = meeting.end_time ? Date.parse(meeting.end_time) : NaN
        const scheduled = scheduledEndRef.current
        const movedEarlier = scheduled === null || end < scheduled - 1000
        if (Number.isFinite(end) && end <= Date.now() && movedEarlier) fire("ended")
      } catch (err: any) {
        if (disposed) return
        // The meeting endpoint stops serving a meeting once it has ended (or was deleted); the
        // explicit `meeting.cancelled` event is the only signal that distinguishes a cancellation.
        const status = err?.response?.status
        if (status === 404 || status === 410) fire("ended")
      }
    }
    const intervalId = window.setInterval(() => void check(), MEETING_END_POLL_MS)
    return () => {
      disposed = true
      window.clearInterval(intervalId)
    }
  }, [canPoll, enabled, fire, meetingId])

  return { handleMeetingEvent }
}
